import type { IObservableArray } from 'mobx';
import { Observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useContext, useEffect } from 'react';

import type {
  FacebookCampaign,
  IConfig,
  IFacebookCampaignInsightsParams,
  Segment,
  Segments as SegmentsCollection,
  TAdChannelKey,
  Targeting,
  TrackedLinkCampaign,
  TTDCampaign,
} from '@feathr/blackbox';
import {
  AggregatedStat,
  CampaignClass,
  EIntegrationTypes,
  isEmailMarketingCampaign,
  isSingularEmailCampaign,
  OmniCampaign,
  reportModuleLabels,
} from '@feathr/blackbox';
import { moment } from '@feathr/hooks';
import { useStore } from '@feathr/report_components/state';
import type { IReportProps } from '@feathr/report_components/types';

import { ActivityBreakdownCard } from '../ActivityBreakdownCard';
import ActivityCard from '../ActivityCard';
import ActivitySummaryCard from '../ActivitySummaryCard';
import BudgetCard from '../BudgetCard';
import CampaignEngagementCard from '../CampaignEngagementCard/CampaignEngagementCard';
import ConversionTableCard from '../ConversionTableCard';
import CreativeTableCard from '../CreativeTableCard';
import DailyStatsTableCard from '../DailyStatsTableCard';
import { DonationROICard } from '../DonationROICard';
import EmailActivitySummaryCard from '../EmailActivitySummaryCard';
import FormSubmissionsTableCard from '../FormSubmissionsTableCard';
import HeatmapCard from '../HeatmapCard';
import KeywordStatsTableCard from '../KeywordStatsTableCard';
import LeadsSummaryCard from '../LeadsSummaryCard';
import LinkClicksTableCard from '../LinkClicksTableCard';
import LoadingMessage from '../LoadingMessage';
import PartnersStatsTableCard from '../PartnersStatsTableCard/PartnersStatsTableCard';
import { ReportContext } from '../Report.context';
import { ROICard } from '../ROICard';
import TargetAudienceOverview, { useAudienceStats } from '../TargetAudienceOverview';
import TrafficBreakdownCard from '../TrafficBreakdownCard';
import { useUniqueReachableEmails } from './CampaignReport.useUniqueReachableEmails';
import { resolveChannelAggregatedStats, resolveChannelDailyStats } from './CampaignReport.utils';
import FacebookCampaignAlerts from './FacebookCampaignAlerts';

import * as styles from './CampaignReport.css';

export function getTargetSegments(
  targetings: IObservableArray<Targeting>,
  Segments: SegmentsCollection,
): Segment[] {
  return targetings
    .filter(
      (t) =>
        t.get('kind') === 'segment' &&
        !!t.get('target_data') &&
        !t.get('is_archived') &&
        t.get('included'),
    )
    .map((t) => Segments.get(t.get('target_data')!));
}

function CampaignReport({
  activeChannel,
  config = {
    includeROI: false,
    includeActivitySummary: true,
    includeSpendPerformance: true,
    includeHeatmap: true,
    includeAdActivity: true,
    includeEmailActivity: true,
    includePageActivity: true,
    includeGoogleActivity: true,
    includeActivityBreakdown: true,
    includeConversionsTable: false,
    includeCreativesTable: true,
    includeDailyStatsTable: true,
    includeLeadsSummary: true,
    includeFormSubmissionsTable: true,
    includeTrafficBreakdown: true,
    includeMonetizationROI: false,
    includeLinkClicksTable: true,
    includeKeywordStatsTable: true,
    attributionModel: 'full',
  } as IConfig,
  end,
  hideReachBreakdown,
  localUrl,
  hasGoals,
  modelId,
  start,
}: Readonly<IReportProps>): JSX.Element {
  return (
    <Observer>
      {function useAnonymousFunction(): JSX.Element {
        const store = useStore();
        const { AggregatedStats, Campaigns, FacebookCampaignInsights, Stats, Targetings, Goals } =
          store;
        const { prioritizeVerifiedActivity, setIsLoading } = useContext(ReportContext) ?? {};

        const pageSize =
          Math.max(
            Math.ceil(
              moment
                .duration(moment.utc(end, moment.ISO_8601).diff(moment.utc(start, moment.ISO_8601)))
                .asDays(),
            ),
            0,
          ) + 1;

        const campaign = Campaigns.get(modelId);
        const { isSegmentCampaign, isOmniCampaign, isOmniReachCampaign } = campaign;

        let campaignDailyStats = Stats.newListResponse();
        const goals = Goals.list({
          filters: {
            _parent: modelId,
            is_archived__ne: true,
          },
        });
        let targetings = Targetings.newListResponse();

        if (!campaign.isPending) {
          campaignDailyStats = Stats.list({
            filters: {
              metadata__date__gte: start,
              metadata__date__lte: end,
              metadata__obj_id: modelId,
            },
            model: 'campaign',
            ordering: ['metadata.date'],
            pagination: {
              page_size: pageSize,
            },
          });
          if (isSegmentCampaign || campaign.isEmailMappingCampaign) {
            targetings = Targetings.list({
              filters: {
                _parent: campaign.id,
              },
            });
          } else {
            targetings.isPending = false;
          }
        }

        const aggregatedStats = AggregatedStats.aggregate({
          filters: {
            metadata__date__gte: start,
            metadata__date__lte: end,
            metadata__obj_id: modelId,
          },
          model: 'campaign',
        });

        const effectiveStats = prioritizeVerifiedActivity
          ? aggregatedStats
          : new AggregatedStat({
              ...aggregatedStats.toJS(),
              ...aggregatedStats.get('unfiltered', {}),
            });

        const resolvedStats = resolveChannelAggregatedStats(effectiveStats, activeChannel);
        const resolvedDailyStats = resolveChannelDailyStats(
          campaignDailyStats.models,
          activeChannel,
        );

        // `activeChannel` may now carry a drip mailing_id; the ad-channel cards only understand
        // tradedesk/meta, so narrow before handing it to them.
        const adChannel: TAdChannelKey | undefined =
          activeChannel === 'tradedesk' || activeChannel === 'meta'
            ? (activeChannel as TAdChannelKey)
            : undefined;

        const targetedAudienceData = useAudienceStats({
          campaign: campaign as TTDCampaign,
          targetings,
          dailyStats: resolvedStats,
          activeChannel: adChannel,
        });

        const targetedEmailAudienceData = useUniqueReachableEmails({
          targetings,
          campaign,
        });

        const pending =
          campaign.isPending ||
          campaignDailyStats.isPending ||
          targetings.isPending ||
          goals.isPending ||
          aggregatedStats.isPending;

        useEffect(() => {
          setIsLoading?.(pending);
        }, [pending, setIsLoading]);

        if (pending) {
          return <LoadingMessage />;
        }

        let budget = 0;
        const exposureSettings = campaign.get('exposure_settings');
        if (exposureSettings.target_type === 'fixed_budget') {
          budget = exposureSettings.target_value!;
        } else if (exposureSettings.target_type === 'fixed_impressions') {
          budget = exposureSettings.target_value! / 250;
        }
        let totalUsers = 0;
        if (campaign.isSegmentCampaign) {
          totalUsers =
            (targetedAudienceData.numTargetedReachableUsers ?? 0) +
            (targetedAudienceData.actualLookalikeAudienceSize ?? 0);
        } else if (campaign.isEmailMappingCampaign) {
          totalUsers = targetedEmailAudienceData.numUniqueEmails ?? 0;
        }

        const conversions = resolvedStats.get('conversions');
        const keywords = resolvedStats.get('terms');

        const numConversions = campaign.isGoogle
          ? resolvedStats.get('num_conversions') || 0
          : conversions?.[config.attributionModel]?.num || 0;
        const facebookCampaignStats = campaign.isFacebook
          ? FacebookCampaignInsights.getStats({
              campaign_id: campaign.id,
              date_start: start,
              date_end: end,
            } as IFacebookCampaignInsightsParams)
          : undefined;
        const isMonetization = campaign.get('parent_kind') === 'partner';
        const showSpend = !(isMonetization && !localUrl);
        const showMonetizationROI =
          config.includeMonetizationROI && campaign.get('monetization_value');
        const campaignClass = campaign.get('_cls');
        const isTrackedLink = campaignClass === CampaignClass.TrackedLink;
        const isReferral = campaignClass === CampaignClass.Referral;
        const isLanding = campaignClass === CampaignClass.LandingPage;
        const isDripStep =
          campaignClass === CampaignClass.DripStep || (campaign.isMailrailDrip && !!activeChannel);
        /*
         * Meta-only Pixl+ campaigns never render channel tabs (tabs require both
         * Display + Meta integrations), so the Activity Breakdown card mounts on a
         * tabless Overview view. Without forcing the Meta empty state here, the
         * bar chart would render blank axes because Meta carries no host-level data.
         */
        const isMetaOnlyOmni = campaign instanceof OmniCampaign && campaign.isMetaChannelOnly;
        interface ITextMap {
          clicksLabel?: string;
          clicksTooltip?: string;
          reachLabel?: string;
          reachTooltip?: string;
          uniqueClicksLabel?: string;
          uniqueClicksTooltip?: string;
          viewsLabel?: string;
          viewsTooltip?: string;
        }

        const textMap = new Map<string, ITextMap>([
          [
            CampaignClass.TrackedLink,
            {
              clicksLabel: 'Page Clicks',
              clicksTooltip: 'Total clicks on a page reached through a tracked link.',
              reachTooltip: 'Number of people who arrived at the URL through a tracked link.',
              uniqueClicksLabel: 'Unique Page Clicks',
              uniqueClicksTooltip:
                'Number of people who clicked a link at the URL reached through a tracked link.',
              viewsLabel: 'Page Views',
              viewsTooltip: 'Total visits to a page reached through a tracked link.',
            },
          ],
        ]);

        // Don't show ROI for DripStep campaigns because their goals are on the parent.
        const shouldShowROI =
          config.includeROI && showSpend && hasGoals && !isDripStep && !campaign.isDonation;
        const shouldShowConversionsTable =
          config.includeConversionsTable && hasGoals && !isDripStep;

        /**
         * DONATION PILOT PROGRAM
         *
         * This is a temporary solution to show the donation campaign report.
         * The donation campaign report will be refactored in the future when we have a
         * donation campaign class. Right now, we are using LandingPage class for donation campaigns.
         *
         * We set `campaign.isDonation` to true for donation campaigns.
         *
         * TODO: move this to `/CampaignReportSection.utils.ts`
         */
        if (campaign.isDonation) {
          config.includeHeatmap = true;
          config.includePageActivity = false;
          config.includeActivityBreakdown = false;
          config.includeConversionsTable = true;
          config.includeDailyStatsTable = false;
          config.includeActivitySummary = false;
          config.includeFormSubmissionsTable = false;
        }

        const integrations =
          (campaign.get('integrations') as EIntegrationTypes[] | undefined) ?? [];

        const isOmniCampaignWithoutReachObjective = isOmniCampaign && !isOmniReachCampaign;
        return (
          <>
            {isOmniCampaignWithoutReachObjective && !hideReachBreakdown && (
              <TargetAudienceOverview
                {...targetedAudienceData}
                className={styles.targetAudienceOverview}
                layout={'horizontal'}
                showDisplay={integrations.includes(EIntegrationTypes.Tradedesk)}
                showMeta={integrations.includes(EIntegrationTypes.Meta)}
              />
            )}

            {campaign.isFacebook && !!localUrl && (
              <FacebookCampaignAlerts campaign={campaign as FacebookCampaign} localUrl={localUrl} />
            )}
            {config.includeActivitySummary &&
              (isEmailMarketingCampaign(campaign) ? (
                <EmailActivitySummaryCard
                  aggregatedStats={resolvedStats}
                  attributionModel={config.attributionModel}
                  campaign={campaign}
                />
              ) : (
                <ActivitySummaryCard
                  aggregatedStats={resolvedStats}
                  campaign={campaign}
                  clicksLabel={textMap.get(campaignClass)?.clicksLabel}
                  clicksTooltip={textMap.get(campaignClass)?.clicksTooltip}
                  conversions={numConversions}
                  facebookCampaignStats={facebookCampaignStats}
                  isLoadingReach={
                    targetedAudienceData.isLoading || targetedEmailAudienceData.isLoading
                  }
                  isMonetization={isMonetization}
                  reachedUsers={targetedAudienceData.numReachedUsers}
                  reachLabel={textMap.get(campaignClass)?.reachLabel}
                  reachTooltipText={textMap.get(campaignClass)?.reachTooltip}
                  targetImpressions={campaign.get('exposure_settings').target_value}
                  totalUsers={totalUsers}
                  uniqueClicksLabel={textMap.get(campaignClass)?.uniqueClicksLabel}
                  uniqueClicksTooltipText={textMap.get(campaignClass)?.uniqueClicksTooltip}
                  viewsLabel={textMap.get(campaignClass)?.viewsLabel}
                  viewsTooltip={textMap.get(campaignClass)?.viewsTooltip}
                />
              ))}
            {campaign.isDonation && (
              <DonationROICard
                aggregatedStats={aggregatedStats}
                attributionModel={config.attributionModel}
                goals={goals.models.map((goal) => goal.toJS())}
              />
            )}
            {campaign.isGoogle && config.includeGoogleActivity && (
              <div className={styles.flex}>
                <ActivityCard
                  dailyStats={resolvedDailyStats}
                  label={'Views'}
                  lines={[{ name: 'Views', showStatSummary: false, statsKey: 'num_views' }]}
                />
                <ActivityCard
                  dailyStats={resolvedDailyStats}
                  label={'Clicks'}
                  lines={[{ name: 'Clicks', showStatSummary: false, statsKey: 'num_clicks' }]}
                />
              </div>
            )}
            {isTrackedLink && config.includeTrafficBreakdown && (
              <TrafficBreakdownCard
                aggregatedStats={resolvedStats}
                attributionModel={config.attributionModel}
                campaign={campaign as TrackedLinkCampaign}
              />
            )}
            {(campaign.isAdCampaign || campaign.isGoogle) &&
              showSpend &&
              config.includeSpendPerformance && (
                <BudgetCard
                  aggregatedStats={resolvedStats}
                  budget={budget}
                  conversions={numConversions}
                  monetizationValue={campaign.get('monetization_value')}
                  showBudget={exposureSettings.target_type === 'fixed_budget'}
                  showCPA={hasGoals || campaign.isGoogle}
                  showCPM={!campaign.isGoogle}
                  showMonetizationROI={
                    isMonetization && showMonetizationROI && config.includeMonetizationROI
                  }
                />
              )}
            {config.includeHeatmap && (
              <div style={{ marginBottom: '10px' }}>
                <HeatmapCard
                  activeChannel={adChannel}
                  isMetaOnly={isMetaOnlyOmni}
                  points={resolvedStats.get('locations', [])}
                />
              </div>
            )}
            <div style={{ marginBottom: '10px' }}>
              {!campaign.isGoogle && config.includeAdActivity && (
                <ActivityCard
                  dailyStats={resolvedDailyStats}
                  label={reportModuleLabels.includeAdActivity}
                  lines={[
                    { name: 'Views', statsKey: 'num_views_ad' },
                    { name: 'Clicks', statsKey: 'num_clicks_ad', yAxisId: 'right' },
                    { name: 'CTR', statsKey: 'ctr', showStatSummary: false },
                  ]}
                />
              )}
              {config.includeEmailActivity && (
                <ActivityCard
                  dailyStats={resolvedDailyStats}
                  label={reportModuleLabels.includeEmailActivity}
                  lines={
                    campaign.isAnyEmail
                      ? [
                          { name: 'Sends', statsKey: 'num_sends_email' },
                          { name: 'Deliveries', statsKey: 'num_deliveries_email' },
                          { name: 'Total opens', statsKey: 'num_views_email' },
                          { name: 'Total clicks', statsKey: 'num_clicks_email' },
                        ]
                      : [
                          { name: 'Views', statsKey: 'num_views_email' },
                          { name: 'Clicks', statsKey: 'num_clicks_email', yAxisId: 'right' },
                          { name: 'CTR', statsKey: 'ctr', showStatSummary: false },
                        ]
                  }
                />
              )}
              {campaign.isAnyEmail &&
                !campaign.isPinpointDrip &&
                config.includeCampaignEngagement && (
                  <CampaignEngagementCard
                    activeChannel={activeChannel}
                    aggregatedStats={resolvedStats}
                    campaign={campaign}
                    dateRange={{ end, start }}
                    localUrl={localUrl}
                  />
                )}
              {config.includePageActivity && (
                <ActivityCard
                  dailyStats={resolvedDailyStats}
                  label={reportModuleLabels.includePageActivity}
                  lines={[
                    { name: 'Views', statsKey: 'num_views_page' },
                    { name: 'Clicks', statsKey: 'num_clicks_page', yAxisId: 'right' },
                    { name: 'CTR', statsKey: 'ctr', showStatSummary: false },
                  ]}
                />
              )}
            </div>
            {campaign.isAnyEmail && config.includeLinkClicksTable && (
              <LinkClicksTableCard clicksByUrl={resolvedStats.get('clicks_by_url', {})} />
            )}
            {config.includeActivityBreakdown && !isSingularEmailCampaign(campaign) && (
              <div style={{ marginBottom: '10px' }}>
                <ActivityBreakdownCard
                  activeChannel={adChannel}
                  aggregatedStats={resolvedStats}
                  conversions={numConversions}
                  hosts={adChannel === 'meta' ? undefined : effectiveStats.get('hosts')}
                  isMetaOnly={isMetaOnlyOmni}
                />
              </div>
            )}
            {shouldShowROI && (
              <ROICard
                aggregatedStats={resolvedStats}
                attributionModel={config.attributionModel}
                context={'campaign'}
                goals={goals.models.map((goal) => goal.toJS())}
              />
            )}
            {shouldShowConversionsTable && (
              <ConversionTableCard
                attributionModel={config.attributionModel}
                end={end}
                goals={goals.models.map((goal) => goal.toJS())}
                localUrl={localUrl}
                model={campaign}
                start={start}
                targetings={targetings.models}
              />
            )}
            {campaign.isAdCampaign && config.includeCreativesTable && (
              <CreativeTableCard campaign={campaign} end={end} start={start} />
            )}
            {!campaign.isFacebook && campaign.isAdCampaign && config.includeCampaignEngagement && (
              <CampaignEngagementCard
                activeChannel={adChannel}
                aggregatedStats={resolvedStats}
                campaign={campaign}
                dateRange={{ end, start }}
                localUrl={localUrl}
              />
            )}
            {isReferral && config.includeLeadsSummary && (
              <LeadsSummaryCard
                attributionModel={config.attributionModel}
                campaign={campaign}
                end={end}
                start={start}
              />
            )}
            {(isReferral || isLanding) && config.includeFormSubmissionsTable && (
              <FormSubmissionsTableCard
                campaign={campaign}
                end={end}
                localUrl={localUrl}
                start={start}
              />
            )}
            {/* DailyStatsTableCard expects ListResponse, not IObservableArray, so it cannot
                use resolvedDailyStats directly. Omni campaigns disable this table via config,
                but if enabled in the future, this should be updated to pass channel-filtered stats. */}
            {config.includeDailyStatsTable && (
              <DailyStatsTableCard
                attributionModel={config.attributionModel}
                dailyStats={campaignDailyStats}
                end={end}
                object={campaign}
                showConversions={hasGoals}
                showSpend={showSpend}
                start={start}
              />
            )}
            {config.includePartnersStatsTable && (
              <PartnersStatsTableCard
                attributionModel={config.attributionModel}
                campaignId={campaign.id}
                end={end}
                localUrl={localUrl}
                start={start}
              />
            )}
            {campaign.isGoogle && config.includeKeywordStatsTable && (
              <KeywordStatsTableCard campaign={campaign} keywords={keywords} />
            )}
          </>
        );
      }}
    </Observer>
  );
}

export default CampaignReport;
